import { useState, useEffect } from 'react'
import API from '../../service/api'
import { Button } from '../../components/atom/Button'
import { saveTikTokToken, removeTikTokToken, isTikTokLoggedIn } from '../../service/tiktok'
import useTranslation from '../../hooks/useTranslation'

export const Tiktok = () => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [tokenData, setTokenData] = useState<{
    access_token?: string
    refresh_token?: string
    expires_in?: number
  } | null>(null)

  useEffect(() => {
    const handleAuthCallback = () => {
      const urlParams = new URLSearchParams(window.location.search)
      const accessToken = urlParams.get('access_token')
      const refreshToken = urlParams.get('refresh_token')
      const expiresIn = urlParams.get('expires_in')

      if (accessToken && refreshToken && expiresIn) {
        const tokenInfo = {
          access_token: accessToken,
          refresh_token: refreshToken,
          expires_in: parseInt(expiresIn)
        }
        setTokenData(tokenInfo)
        localStorage.setItem('tiktok_token', JSON.stringify(tokenInfo))
        window.history.replaceState({}, document.title, window.location.pathname)
      }
    }

    if (window.location.pathname === '/success' || window.location.search.includes('access_token')) {
      handleAuthCallback()
    }

    if (isTikTokLoggedIn()) {
      const savedToken = localStorage.getItem('tiktok_token')
      if (savedToken) {
        try {
          setTokenData(JSON.parse(savedToken))
        } catch (error) {
          removeTikTokToken()
          localStorage.removeItem('tiktok_token')
        }
      }
    }
  }, [])

  const handleTiktokLogin = async () => {
    setIsLoading(true)
    try {
      const response = await API.get('/auth/tiktok/login')
      const { authUrl } = response.data || response
      
      const popup = window.open(
        authUrl,
        'tiktokAuth',
        'width=500,height=600,scrollbars=yes,resizable=yes'
      )

      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed)
          setIsLoading(false)
          window.location.reload()
        }
      }, 1000)

      const messageHandler = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return
        
        if (event.data.type === 'TIKTOK_AUTH_SUCCESS') {
          const { tokenData: tokens } = event.data
          setTokenData(tokens)
          localStorage.setItem('tiktok_token', JSON.stringify(tokens))
          saveTikTokToken(tokens.access_token)
          popup?.close()
          clearInterval(checkClosed)
          setIsLoading(false)
          window.removeEventListener('message', messageHandler)
        }
      }

      window.addEventListener('message', messageHandler)

    } catch (error) {
      alert(t('tiktok.messages.loginFailed') + (error as Error).message)
      setIsLoading(false)
    }
  }

  const handleLogout = () => {
    setTokenData(null)
    localStorage.removeItem('tiktok_token')
    removeTikTokToken()
  }

  const handleUpload = async () => {
    if (!tokenData?.access_token) {
      alert(t('tiktok.messages.loginFirst'))
      return
    }
    
    alert(t('tiktok.messages.uploadComingSoon'))
  }

  return (
    <div className="p-8 max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-2xl font-bold mb-6 text-center">{t('tiktok.title')}</h1>
        
        {tokenData ? (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h2 className="text-lg font-semibold text-green-800 mb-2">{t('tiktok.connection.success')}</h2>
              <p className="text-green-600">{t('tiktok.connection.successMessage')}</p>
              <div className="mt-2 text-sm text-gray-600">
                <p>{t('tiktok.connection.tokenExpiry')}: {tokenData.expires_in ? new Date(Date.now() + tokenData.expires_in * 1000).toLocaleString() : 'N/A'}</p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={handleUpload}
                className="bg-pink-500 hover:bg-pink-600 text-white px-6 py-3 rounded-lg font-medium flex-1"
              >
                {t('tiktok.buttons.uploadVideo')}
              </Button>
              
              <Button
                onClick={handleLogout}
                className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-medium"
              >
                {t('tiktok.buttons.disconnect')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-center">
              <div className="text-6xl mb-4">🎵</div>
              <h2 className="text-xl font-semibold mb-2">{t('tiktok.intro.title')}</h2>
              <p className="text-gray-600 mb-6">{t('tiktok.intro.subtitle')}</p>
            </div>
            
            <Button
              onClick={handleTiktokLogin}
              disabled={isLoading}
              className="w-full bg-black hover:bg-gray-800 text-white py-4 px-6 rounded-lg font-medium text-lg transition-colors disabled:opacity-50"
            >
              {isLoading ? t('tiktok.buttons.loginInProgress') : t('tiktok.buttons.login')}
            </Button>
            
            <div className="text-center text-sm text-gray-500">
              <p>{t('tiktok.intro.instruction')}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
